import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { PermissionService } from '../../../../services/PermissionService';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { preferenceAtom } from '../../../../util/preferences-service-v3';
import {
  AuditorDateRangesPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../../data-models/preferences-v3.data-model';

const auditorDateRangesPreferenceAtom = preferenceAtom<AuditorDateRangesPreferences>({
  preferenceKey: PreferenceKey.auditorDateRanges,
  scope: PreferenceScope.ORGANIZATION,
});

export function useAuditorDateConstraints() {
  const hasAuditorAccess = PermissionService.get().hasPermission(PermissionKey.hasAuditorAccess);
  const preferences = useAtomValue(auditorDateRangesPreferenceAtom);

  return useMemo(() => {
    if (!hasAuditorAccess || !preferences?.restrictToRange) {
      return null;
    }

    return {
      startDate: preferences.startDate,
      endDate: preferences.endDate,
      defaultFrequency: preferences.defaultFrequency,
    };
  }, [hasAuditorAccess, preferences]);
}

/**
 * Parse quarter string (e.g., "2024-Q1") or year-only string (e.g., "2024") to year and quarter number.
 * For year-only strings:
 * - When used as startQuarter: treats as Q1 (start of year)
 * - When used as endQuarter: treats as Q4 (end of year)
 *
 * @param quarterStr - Quarter string in format "YYYY-QN" or "YYYY"
 * @param isEndQuarter - If true and year-only format, defaults to Q4; if false, defaults to Q1
 */
export function parseQuarter(
  quarterStr: string,
  isEndQuarter: boolean = false
): { year: number; quarter: number } | null {
  // Try to match quarter format first (e.g., "2024-Q1")
  const quarterMatch = quarterStr.match(/^(\d{4})-Q([1-4])$/);
  if (quarterMatch) {
    return {
      year: parseInt(quarterMatch[1], 10),
      quarter: parseInt(quarterMatch[2], 10),
    };
  }

  // Try to match year-only format (e.g., "2024")
  const yearMatch = quarterStr.match(/^(\d{4})$/);
  if (yearMatch) {
    return {
      year: parseInt(yearMatch[1], 10),
      quarter: isEndQuarter ? 4 : 1, // Q4 for end dates, Q1 for start dates
    };
  }

  return null;
}

/**
 * Calculate number of quarters between two quarter/year strings
 */
export function calculateQuartersBetween(startQuarter: string, endQuarter: string): number {
  const start = parseQuarter(startQuarter, false); // Start quarter defaults to Q1 for year-only
  const end = parseQuarter(endQuarter, true); // End quarter defaults to Q4 for year-only

  if (!start || !end) {
    return 0;
  }

  const startTotal = start.year * 4 + start.quarter;
  const endTotal = end.year * 4 + end.quarter;

  return endTotal - startTotal + 1; // +1 to include both endpoints
}

/**
 * Convert quarter/year string to the first day of that quarter/year
 * Examples:
 * - "2024-Q1" → Date(2024, 0, 1) (January 1, 2024)
 * - "2024-Q4" → Date(2024, 9, 1) (October 1, 2024)
 * - "2024" → Date(2024, 0, 1) (January 1, 2024)
 */
export function quarterToStartDate(quarterStr: string): Date | null {
  const parsed = parseQuarter(quarterStr, false); // Start quarters default to Q1 for year-only
  if (!parsed) {
    return null;
  }

  const { year, quarter } = parsed;

  // First day of the quarter: (quarter - 1) * 3 months
  return new Date(year, (quarter - 1) * 3, 1);
}

/**
 * Convert quarter/year string to the last day of that quarter/year
 * Examples:
 * - "2024-Q1" → Date(2024, 2, 31) (March 31, 2024)
 * - "2024-Q4" → Date(2024, 11, 31) (December 31, 2024)
 * - "2024" → Date(2024, 11, 31) (December 31, 2024)
 */
export function quarterToEndDate(quarterStr: string): Date | null {
  const parsed = parseQuarter(quarterStr, true); // End quarters default to Q4 for year-only
  if (!parsed) {
    return null;
  }

  const { year, quarter } = parsed;

  // Last day of the quarter: quarter * 3 months, day 0 (last day of previous month)
  return new Date(year, quarter * 3, 0);
}
